import { Link } from "@inertiajs/react"

import { RaceShield } from "@/components/race-shield"
import { TeamName } from "@/components/team-name"
import { getTeamInitials } from "@/lib/race-colors"
import { leagueMatchPath } from "@/routes"
import type { RecentResult } from "@/types"

function wdlBorderColor(result: "win" | "loss" | "draw" | null): string {
  if (result === "win") return "oklch(0.55 0.15 145)"
  if (result === "loss") return "oklch(0.55 0.15 28)"
  if (result === "draw") return "oklch(0.55 0.08 55)"
  return "transparent"
}

function ScoreDiamond() {
  return (
    <svg
      viewBox="0 0 10 10"
      className="mx-1.5 inline-block h-2.5 w-2.5 flex-shrink-0"
    >
      <polygon points="5,0 10,5 5,10 0,5" fill="url(#ticker-diamond)" />
    </svg>
  )
}

export function ResultsTicker({
  leagueId,
  results,
}: {
  leagueId: string
  results: RecentResult[]
}) {
  const repeatCount = Math.max(2, Math.ceil(3840 / (results.length * 380)))
  const items = Array.from({ length: repeatCount }, () => results).flat()
  const duration = Math.max(results.length * 12, 40)

  return (
    <section
      className="ticker-bar group fixed right-0 bottom-0 left-0 z-40 flex h-[76px] items-center overflow-hidden"
      style={{
        background: "oklch(0.10 0.005 230)",
        boxShadow:
          "inset 0 1px 0 oklch(0.25 0.01 230), inset 0 -1px 0 oklch(0.06 0.005 230), 0 2px 8px oklch(0 0 0 / 0.6)",
      }}
    >
      {/* Shared SVG defs for diamond gradient */}
      <svg className="absolute h-0 w-0" aria-hidden="true">
        <defs>
          <linearGradient id="ticker-diamond" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="oklch(0.38 0.14 85)" />
            <stop offset="100%" stopColor="oklch(0.25 0.08 85)" />
          </linearGradient>
        </defs>
      </svg>

      {/* Top gold line */}
      <span
        className="pointer-events-none absolute top-0 right-0 left-0 h-px animate-[gold-breathe_6s_ease-in-out_infinite] motion-reduce:animate-none"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, oklch(0.85 0.28 85) 30%, oklch(0.85 0.28 85) 70%, transparent 100%)",
        }}
      />
      {/* Bottom dim gold line */}
      <span
        className="pointer-events-none absolute right-0 bottom-0 left-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, oklch(0.65 0.2 85 / 0.4) 30%, oklch(0.65 0.2 85 / 0.4) 70%, transparent 100%)",
        }}
      />

      {/* RESULTS label */}
      <span
        className="font-display flex h-full flex-shrink-0 items-center px-4 text-[11px] font-bold tracking-[0.15em] whitespace-nowrap uppercase"
        style={{
          background: "oklch(0.08 0.005 230)",
          borderRight: "1px solid oklch(0.20 0.01 230)",
          borderLeft: "4px solid oklch(0.85 0.28 85 / 0.6)",
          color: "oklch(0.85 0.28 85)",
        }}
      >
        RESULTS
      </span>

      {/* Scrolling area */}
      <div
        className="flex flex-1 items-center overflow-hidden"
        style={{
          height: "100%",
          maskImage:
            "linear-gradient(to right, transparent 0px, black 32px, black calc(100% - 32px), transparent 100%)",
        }}
      >
        <div
          className="animate-ticker-scroll flex items-center whitespace-nowrap will-change-transform group-hover:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:overflow-x-auto"
          style={
            {
              "--ticker-duration": `${duration}s`,
            } as React.CSSProperties
          }
        >
          {items.map((result, i) => (
            <span key={`${result.id}-${i}`} className="flex items-center">
              {i > 0 && (
                <span
                  className="mx-1.5 h-10 w-px flex-shrink-0"
                  style={{ background: "oklch(0.25 0.08 85 / 0.4)" }}
                />
              )}
              <Link
                href={leagueMatchPath(leagueId, result.id)}
                className="flex flex-shrink-0 flex-col justify-center px-5 py-1.5 no-underline transition-colors duration-150 hover:bg-[oklch(0.15_0.008_230)]"
                style={{
                  borderLeft: `2px solid ${wdlBorderColor(result.result)}`,
                  minWidth:
                    (result.home_team_name.length +
                      result.away_team_name.length) *
                      8 +
                    160,
                }}
              >
                {/* Top row: shields + teams + score */}
                <span className="flex w-full items-center justify-between gap-3">
                  {/* Home side */}
                  <span className="flex items-center gap-2">
                    <RaceShield
                      race={result.home_team_race}
                      hue={result.home_team_color_hue}
                      label={getTeamInitials(result.home_team_name)}
                      size="xs"
                    />
                    <span className="text-sm font-semibold whitespace-nowrap">
                      <TeamName
                        name={result.home_team_name}
                        hue={result.home_team_color_hue}
                      />
                    </span>
                  </span>

                  {/* Score center */}
                  <span className="flex flex-shrink-0 items-center">
                    <span
                      className="font-display text-lg leading-none font-bold"
                      style={{
                        color: "oklch(0.85 0.28 85)",
                        textShadow:
                          "0 1px 0 oklch(0.4 0.04 70), 0 1px 3px oklch(0 0 0 / 0.4)",
                      }}
                    >
                      {result.home_score}
                    </span>
                    <ScoreDiamond />
                    <span
                      className="font-display text-lg leading-none font-bold"
                      style={{
                        color: "oklch(0.85 0.28 85)",
                        textShadow:
                          "0 1px 0 oklch(0.4 0.04 70), 0 1px 3px oklch(0 0 0 / 0.4)",
                      }}
                    >
                      {result.away_score}
                    </span>
                  </span>

                  {/* Away side */}
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold whitespace-nowrap">
                      <TeamName
                        name={result.away_team_name}
                        hue={result.away_team_color_hue}
                      />
                    </span>
                    <RaceShield
                      race={result.away_team_race}
                      hue={result.away_team_color_hue}
                      label={getTeamInitials(result.away_team_name)}
                      size="xs"
                    />
                  </span>
                </span>

                {/* Bottom row: round + headline */}
                <span className="mt-0.5 flex items-center gap-1.5 text-sm whitespace-nowrap">
                  <span
                    className="font-display flex-shrink-0 font-bold tracking-wider uppercase"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    RD {result.round_number}
                  </span>
                  {result.ticker_headline && (
                    <>
                      <span style={{ color: "var(--muted-foreground)" }}>
                        ·
                      </span>
                      <span
                        className="italic"
                        style={{ color: "oklch(0.55 0.04 85)" }}
                      >
                        {result.ticker_headline}
                      </span>
                    </>
                  )}
                </span>
              </Link>
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}
